import { useEffect, useMemo, useState } from 'react';

import styles from '../../css/admin/NoticeBoard.module.css';
import { useLanguage } from '../../hooks/LanguageContext';
import useAxiosAdminFetch from '../../hooks/useAxiosAdminFetch';
import translations from '../admin/translations';
import { sanitizeNoticeHtml } from '../../utils/sanitizeNoticeHtml';

/**
 * [notice-002] 버튼 링크 검사. 서버 DTO(NOTICE_BUTTON_URL_REGEX)와 같은 규칙 — https:// 전체 주소만.
 * ★서버가 이미 거르지만 DB 에 직접 들어간 값까지 믿지 않는다.
 */
const BUTTON_URL_RE = /^https:\/\/[^\s\\/?#]+([/?#][^\s\\]*)?$/;

/**
 * [notice-002] 상점관리자 공지 상세(/mobile/notice?id=N). NoticeBoard.js 가 ?id 가 있을 때 렌더한다.
 *  · 노출 규칙은 목록과 같다(API). 막힌 공지·없는 번호는 404 → "존재하지 않는 공지입니다."
 *  · 버튼은 문구·링크가 둘 다 있고 링크가 https:// 일 때만 하단에 보인다. 같은 창에서 연다(앱 웹뷰 동작 그대로).
 * ★원본 content 는 절대 그대로 렌더하지 않는다 — 항상 sanitizeNoticeHtml 을 거친다.
 */
export default function NoticeDetail({ id, formatDate, onList }) {
  const { fetchData } = useAxiosAdminFetch();
  const { language } = useLanguage();
  const t = translations[language];

  const validId = /^\d+$/.test(id || '');
  const [notice, setNotice] = useState(null);
  // loading / notFound / error 는 서로 배타적이다.
  const [loading, setLoading] = useState(validId);
  const [notFound, setNotFound] = useState(!validId);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!validId) {
      setNotice(null);
      setNotFound(true);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setNotFound(false);
      setError(false);
      try {
        // 훅은 오류를 던지지 않고 error.response 를 돌려준다(useAxiosAdminFetch.js:237).
        const response = await fetchData({
          method: 'GET',
          url: `${process.env.REACT_APP_API_SERVER}notice/${id}`,
        });
        if (cancelled) return;
        const data = response?.data?.data;
        if (response?.status === 404) {
          setNotFound(true);
        } else if (!data) {
          // 401 이면 훅이 이미 로그인 이동을 처리한다.
          setError(true);
        } else {
          setNotice(data);
        }
      } catch (e) {
        console.error('notice detail load error:', e);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [fetchData, id, validId]);

  const sanitizedContent = useMemo(
    () => sanitizeNoticeHtml(notice?.content, notice?.contentFormat),
    [notice?.content, notice?.contentFormat]
  );
  const dateText = formatDate(notice?.startDttm);
  const showButton =
    !!notice?.buttonText && BUTTON_URL_RE.test(notice?.buttonUrl || '');

  if (loading) {
    return (
      <div className={styles.empty}>
        {t.noticeBoardLoading || '불러오는 중...'}
      </div>
    );
  }
  if (notFound) {
    return (
      <>
        <div className={styles.empty}>
          {t.noticeBoardNotFound || '존재하지 않는 공지입니다.'}
        </div>
        <button type="button" className={styles.moreButton} onClick={onList}>
          {t.noticeBoardToList || '목록으로'}
        </button>
      </>
    );
  }
  if (error || !notice) {
    return (
      <div className={styles.empty}>
        {t.noticeBoardError ||
          '공지를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.'}
      </div>
    );
  }

  return (
    <article>
      <div className={styles.detailHead}>
        <h2 className={styles.detailTitle}>{notice.title}</h2>
        {dateText && <span className={styles.itemDate}>{dateText}</span>}
      </div>
      <div
        className={`${styles.body} ${styles.detailBody}`}
        dangerouslySetInnerHTML={{ __html: sanitizedContent }}
      />
      {showButton && (
        <a className={styles.detailButton} href={notice.buttonUrl}>
          {notice.buttonText}
        </a>
      )}
    </article>
  );
}
